import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import useStore from '@hooks/useStore';
import PaymentSuccessGraphics from '@components/PaymentSuccessfulGraphics';
import ConfettiAnimation from '@components/ConfettiAnimation';
import { PaymentLinkStatus, PAYMENT_LINK_PAYMENT_METHODS } from '@constants/paymentLink';
import { useStyles } from './styles';

const PaymentSuccess: React.FC = () => {
  const {
    links: {
      coBrandingConfig: { primaryColor },
    },
    checkout,
    links,
  } = useStore();
  const classes = useStyles({ primaryColor });
  const { t } = useTranslation();
  const { paymentLink } = links;
  const { settings, isSubscription, status, paymentMethod } = paymentLink;

  const isSubscriptionCanceled = status === PaymentLinkStatus.SUBSCRIPTION_CANCELED;
  const isSubscriptionPendingActivation = status === PaymentLinkStatus.PENDING_ACTIVATION;
  const isDirectDebit = paymentMethod === PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT;

  const handleRedirect = () => {
    if (settings?.redirectAuto === 'true') {
      window.location.replace(settings?.redirectUrl);
    }
  };

  const isCardPaymentSuccess = checkout.success.cardPayment;
  const isDirectDebitPaymentSuccess = checkout.success.directDebitPayment;

  useEffect(() => {
    if (isCardPaymentSuccess || isDirectDebitPaymentSuccess) {
      handleRedirect();
      links.setAsPaid();
    }
  }, [isCardPaymentSuccess, isDirectDebitPaymentSuccess]);

  return (
    <div className={classes.root}>
      {isCardPaymentSuccess && !isSubscriptionCanceled && <ConfettiAnimation />}
      {!isSubscriptionCanceled && (
        <>
          <h2 className={classes.title}>
            {!isSubscription
              ? t('checkoutScreen.payment.success.title')
              : !isSubscriptionPendingActivation
              ? t('checkoutScreen.subscription.success.title')
              : t('checkoutScreen.subscription.pending.title')}
          </h2>
          <div className={classes.message}>
            {!isSubscription
              ? t('checkoutScreen.payment.success.message')
              : !isSubscriptionPendingActivation
              ? isDirectDebit
                ? t('checkoutScreen.subscription.success.directDebitMessage')
                : t('checkoutScreen.subscription.success.message')
              : t('checkoutScreen.subscription.pending.message')}
          </div>
        </>
      )}
      {isSubscriptionCanceled && (
        <>
          <h2 className={classes.title}>{t('checkoutScreen.subscription.canceled.title')}</h2>
          <div className={classes.message}>{t('checkoutScreen.subscription.canceled.message')}</div>
        </>
      )}
      <div className={classes.imageContainer}>
        <PaymentSuccessGraphics fill={primaryColor} />
      </div>
    </div>
  );
};

export default observer(PaymentSuccess);
